import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import Footer from '../../components/Footer';
import { FiCalendar, FiChevronRight } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL } from '../../config/config';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    fetchBlogPost();
    fetchRelatedPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages/blog`);
      const data = await response.json();
      
      if (data.success) {
        const foundPost = data.data.find(p => p.url === slug);
        if (foundPost) {
          setPost(foundPost);
        }
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pages/blog`);
      const data = await response.json();
      
      if (data.success) {
        setRelatedPosts(data.data.slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching related posts:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="bg-gray-200 h-96 rounded-lg mb-8"></div>
            <div className="bg-gray-200 h-8 rounded w-3/4 mb-4"></div>
            <div className="bg-gray-200 h-4 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Blog Post Not Found</h2>
          <Link to="/blog" className="btn-primary inline-block">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-primary-600">Home</Link>
            <FiChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-primary-600">Blog</Link>
            <FiChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{post.title}</span>
          </div>

          {/* Post Header */}
          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>
            
            <div className="flex items-center gap-4 text-gray-600">
              <span className="flex items-center gap-2">
                <FiCalendar className="w-5 h-5" />
                {new Date(post.cdate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          {post.image && (
            <div className="mb-8 rounded-xl overflow-hidden shadow-xl">
              <img
                src={`${BASE_URL}/${post.image}`}
                alt={post.title}
                className="w-full h-96 object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="600"%3E%3Crect fill="%236366f1" width="1200" height="600"/%3E%3C/svg%3E';
                }}
              />
            </div>
          )}

          {/* Post Content */}
          <div className="card prose prose-lg max-w-none">
            <div 
              dangerouslySetInnerHTML={{ __html: post.description }}
              className="text-gray-700 leading-relaxed"
            />
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Related Articles
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.url}`}
                  className="card hover:shadow-xl transition-all group"
                >
                  {relatedPost.image && (
                    <div className="relative mb-4 rounded-lg overflow-hidden">
                      <img
                        src={`${BASE_URL}/${relatedPost.image}`}
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%236366f1" width="400" height="300"/%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  )}
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {relatedPost.short_description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;

